import { injectable } from 'inversify';
import { at, intersection } from 'lodash';

import { ValidationService } from '@vk-hr-tek/core/validation';

import {
  FormAttributeCondition,
  FormAttributeOptions,
  FormAttributeShowWhen,
} from '@app/gen/events';

import { FormAttribute } from '../types';

@injectable()
export class EventsFormAttributesService {
  constructor(private validationService: ValidationService) {}

  checkIsHidden(
    options: FormAttributeOptions = {},
    formValues: Record<string, any> | undefined,
    prefix: string,
  ) {
    const hideWhen = options?.hide_when;
    const showWhen = options?.show_when;
    if (!hideWhen && !showWhen?.conditions) {
      return false;
    }

    if (hideWhen) {
      const attributeValue = at(
        formValues,
        `${prefix}_attribute_${hideWhen.attribute_id}`,
      )[0];

      if (
        !attributeValue ||
        (Array.isArray(attributeValue) && !attributeValue.length)
      ) {
        return true;
      }

      const value = Array.isArray(attributeValue)
        ? attributeValue
        : [attributeValue];

      if (hideWhen.equal) {
        return !!intersection(hideWhen.equal, value).length;
      } else {
        return !intersection(hideWhen.unequal || [], value).length;
      }
    } else if (showWhen?.conditions?.length) {
      return showWhen.conditions.some((item) => {
        const attributeValue = at(
          formValues,
          `${prefix}_attribute_${item.attribute_id}`,
        )[0];

        if (
          !attributeValue ||
          (Array.isArray(attributeValue) && !attributeValue.length)
        ) {
          return true;
        }

        const value = Array.isArray(attributeValue)
          ? attributeValue
          : [attributeValue];

        if (item.equal) {
          return item.equal.some((equalItem) => !value.includes(equalItem));
        } else if (item.unequal) {
          return item.unequal.some((unequalItem) =>
            value.includes(unequalItem),
          );
        }
      });
    } else {
      return false;
    }
  }

  getRequiredValidator(
    message: string,
    options: {
      prefix: string;
      hideWhen?: FormAttributeCondition;
      showWhen?: FormAttributeShowWhen;
    },
  ) {
    if (options?.hideWhen) {
      return this.validationService.requiredIfNotIn(message, {
        field: `${options.prefix}_attribute_${options.hideWhen.attribute_id}`,
        in: options.hideWhen.equal || options.hideWhen.unequal || [],
        operation: options.hideWhen.equal ? 'in' : 'ex',
      });
    }

    if (options?.showWhen) {
      return this.validationService.requiredIfMeetsConditions(message, {
        prefix: options.prefix,
        conditions: options.showWhen.conditions as FormAttributeCondition[],
      });
    }

    return this.validationService.required(message);
  }

  shouldAttributeBeDisabled({
    attribute,
    initialDisabled,
    relatedAttributeValue,
    relatedAttributeHasError,
  }: {
    attribute: FormAttribute;
    relatedAttributeValue: string[] | string;
    initialDisabled: boolean;
    relatedAttributeHasError: boolean;
  }) {
    const { attributeType, relatedAttribute } = attribute;

    if (!relatedAttribute) return initialDisabled;

    const relatedAttributeHasValue = Boolean(
      Array.isArray(relatedAttributeValue)
        ? relatedAttributeValue?.length
        : relatedAttributeValue,
    );

    const isRangeEndWithoutValueOrWithError =
      attributeType === 'rangeEnd' &&
      (!relatedAttributeHasValue || relatedAttributeHasError);

    return isRangeEndWithoutValueOrWithError || initialDisabled;
  }
}
